import { useEffect, useState } from "react";

import { Alert } from "../components/Alert";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Empty } from "../components/Card";
import { Field, Input, Select } from "../components/Field";
import { Modal, ModalForm } from "../components/Modal";
import { MONO } from "../components/Table";
import { fetchApiKeys, issueApiKey, revokeApiKey, rotateApiKey } from "../lib/api";
import { messageOf } from "../lib/errors";
import { formatDate } from "../lib/format";
import { PERM, can } from "../lib/rbac";
import { useAction } from "../lib/useAction";
import type { ApiKey, IssueApiKeyInput, IssuedApiKey, Member, Session } from "../lib/types";

const DAY_MS = 24 * 60 * 60 * 1000;

type ExpiryMode = "default" | "date" | "unlimited";

/** `<input type="date">` 값(YYYY-MM-DD, 로컬). */
const ymd = (d: Date): string =>
  `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * 날짜 입력을 서버의 RFC3339 로 바꿉니다 — 그날 **로컬 자정 직전**까지 유효하게 둡니다.
 * 하드캡(발급 시점 + 365일)을 넘지 않도록 날짜 상한은 364일 뒤로 제한합니다.
 */
const endOfDayISO = (date: string): string => new Date(`${date}T23:59:59`).toISOString();

/** 저장 상태(active|revoked)와 만료 시각으로 표시 상태를 만듭니다 — expired 는 저장값이 아닙니다. */
function keyState(key: ApiKey, now: number): "active" | "expired" | "revoked" {
  if (key.status === "revoked") return "revoked";
  if (key.expiresAt !== null && key.expiresAt <= now) return "expired";
  return "active";
}

/**
 * 서비스 계정의 API 키 관리 모달 — 목록 · 발급 · 교체 · 회수.
 *
 * 평문 키(`pltk_…`)는 발급/교체 응답에서 **한 번만** 나옵니다. 이 모달은 그 값을 컴포넌트 상태에만
 * 두고 닫히면 버립니다(저장·로깅 금지). 복사하지 않고 닫으려 하면 한 번 더 묻습니다 — 평문을 잃으면
 * 쓸 수 없는 활성 키가 남아 회수 후 재발급해야 합니다.
 *
 * 오류는 화면 flash 가 아니라 **모달 안**에 띄웁니다 — flash 는 모달 뒤에 가려 보이지 않습니다.
 *
 * **발급 응답을 기다리는 동안에는 닫히지 않습니다**(닫기 버튼·Escape·배경 클릭 모두). 요청이 나간 뒤
 * 창이 사라지면 서버는 키를 만들었는데 평문을 받을 화면이 없어, 아무도 평문을 갖지 못한 활성 키가
 * 남습니다. 교체·회수는 확인 창 안에서 진행되며 그 창이 같은 규칙으로 닫힘을 막습니다.
 */
export function ApiKeyModal({
  session,
  member,
  onClose,
}: {
  session: Session;
  member: Member;
  onClose: () => void;
}) {
  const mayIssue = can(session, PERM.apiKeyIssue);
  const mayUnlimited = can(session, PERM.apiKeyIssueUnlimited);
  const mayRevoke = can(session, PERM.apiKeyRevoke);
  const isActive = member.status === "active";
  const issue = useAction();

  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [reload, setReload] = useState(0);
  /** 목록 조회·클립보드 실패. 발급 실패는 `issue.error`, 입력 문제는 `formError` 가 따로 듭니다. */
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssuedApiKey | null>(null);
  const [copied, setCopied] = useState(false);

  /** 열린 확인 창 — 한 번에 하나만 뜨도록 한 상태로 둡니다. */
  const [confirm, setConfirm] = useState<
    { kind: "rotate"; key: ApiKey } | { kind: "revoke"; key: ApiKey } | { kind: "discard" } | null
  >(null);

  const [name, setName] = useState("");
  const [mode, setMode] = useState<ExpiryMode>("default");
  const [date, setDate] = useState("");

  useEffect(() => {
    let alive = true;
    fetchApiKeys(member.memberId)
      .then((rows) => {
        if (alive) setKeys(rows);
      })
      .catch((err: unknown) => {
        if (alive) setError(`키 목록 조회 실패: ${messageOf(err)}`);
      });
    return () => {
      alive = false;
    };
  }, [member.memberId, reload]);

  const unsaved = issued !== null && !copied;

  /** 발급 대기 중이면 무시하고, 복사 안 한 평문이 있으면 확인 창을 띄웁니다(Escape·배경 클릭도 이 경로). */
  const close = () => {
    if (issue.pending) return;
    if (unsaved) {
      setConfirm({ kind: "discard" });
      return;
    }
    onClose();
  };

  const showIssued = (out: IssuedApiKey) => {
    setIssued(out);
    setCopied(false);
    setNotice(null);
    setReload((r) => r + 1);
  };

  const onIssue = () => {
    const keyName = name.trim();
    setFormError(null);
    if (!keyName) {
      setFormError("키 이름을 입력하세요.");
      return;
    }
    if (mode === "date" && !date) {
      setFormError("만료일을 고르세요.");
      return;
    }
    const input: IssueApiKeyInput = { name: keyName, memberId: member.memberId };
    if (mode === "date") input.expiresAt = endOfDayISO(date);
    if (mode === "unlimited") input.unlimited = true;
    void issue.run(async () => {
      const out = await issueApiKey(input, session.csrfToken);
      if (!out) throw new Error("발급 응답을 받지 못했습니다.");
      setName("");
      showIssued(out);
    });
  };

  const onRotate = async (key: ApiKey) => {
    const out = await rotateApiKey(key.keyId, session.csrfToken);
    if (!out) throw new Error("교체 응답을 받지 못했습니다.");
    showIssued(out);
  };

  const onRevoke = async (key: ApiKey, reason: string) => {
    await revokeApiKey(key.keyId, reason, session.csrfToken);
    setNotice(`${key.masked} 를 회수했습니다.`);
    setReload((r) => r + 1);
  };

  const onCopy = async (plain: string) => {
    try {
      await navigator.clipboard.writeText(plain);
      setCopied(true);
    } catch {
      setError("클립보드 복사에 실패했습니다. 키를 직접 선택해 복사하세요.");
    }
  };

  const now = Date.now();
  const today = new Date();
  const minDate = ymd(new Date(today.getTime() + DAY_MS));
  const maxDate = ymd(new Date(today.getTime() + 364 * DAY_MS));
  const canIssue = mayIssue && isActive;

  return (
    <Modal
      title={`${member.displayName} API 키`}
      sub={
        member.owningSystem
          ? `소속 시스템 ${member.owningSystem} · member #${String(member.memberId)}`
          : `member #${String(member.memberId)}`
      }
      onClose={close}
      footer={
        <>
          <Button variant="ghost" disabled={issue.pending} onClick={close}>
            닫기
          </Button>
          {canIssue ? (
            <Button pending={issue.pending} onClick={onIssue}>
              발급
            </Button>
          ) : null}
        </>
      }
      status={
        issue.error ? (
          <Alert kind="error">{`발급 실패: ${issue.error}`}</Alert>
        ) : formError ? (
          <Alert kind="warn">{formError}</Alert>
        ) : null
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      {notice ? <Alert kind="ok">{notice}</Alert> : null}

      {issued ? (
        <div
          aria-label="새 API 키"
          className="mb-[16px] flex flex-col gap-[8px] rounded-xl border border-[#ffe3c2] bg-[#fff6eb] p-[14px]"
        >
          <div className="text-[13px] font-bold text-[#a65400]">
            새 키는 지금 한 번만 보입니다. 바로 복사해 보관하세요.
          </div>
          <div className="flex gap-[8px]">
            <Input
              aria-label="발급된 API 키"
              readOnly
              className={`${MONO} min-w-0 flex-1`}
              value={issued.apiKey}
              onFocus={(ev) => ev.currentTarget.select()}
            />
            {/* 입력칸(40px)과 같은 md. 라벨이 "복사됨" 으로 바뀌어도 폭이 흔들리지 않게 최소 폭을 둡니다. */}
            <Button className="min-w-[80px]" onClick={() => void onCopy(issued.apiKey)}>
              {copied ? "복사됨" : "복사"}
            </Button>
          </div>
          <div className="text-[12px] text-grey-600">
            {`${issued.masked} · ${issued.expiresAt === null ? "무기한" : `만료 ${formatDate(issued.expiresAt)}`}`}
          </div>
        </div>
      ) : null}

      {keys === null && !error ? <Empty>불러오는 중…</Empty> : null}
      {keys !== null && keys.length === 0 ? <Empty>발급된 키가 없습니다.</Empty> : null}
      {keys !== null && keys.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-[8px] p-0">
          {keys.map((k) => {
            const state = keyState(k, now);
            return (
              <li
                key={k.keyId}
                className="flex items-center gap-[10px] rounded-xl border border-grey-100 px-[12px] py-[10px]"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-[6px]">
                    <span className="text-[13px] font-bold">{k.name}</span>
                    {state === "active" ? (
                      <Badge tone="ok">활성</Badge>
                    ) : state === "expired" ? (
                      <Badge tone="muted">만료</Badge>
                    ) : (
                      <Badge tone="danger">회수됨</Badge>
                    )}
                    {k.replacedBy && state === "active" ? <Badge tone="warn">교체됨</Badge> : null}
                  </div>
                  <div className={`${MONO} text-grey-600`}>{k.masked}</div>
                  <div className="text-[11px] text-grey-400">
                    {[
                      `발급 ${formatDate(k.createdAt)}`,
                      k.expiresAt === null ? "무기한" : `만료 ${formatDate(k.expiresAt)}`,
                      k.lastUsedAt ? `최근 사용 ${formatDate(k.lastUsedAt)}` : "사용 기록 없음",
                      k.revokeReason ? `사유: ${k.revokeReason}` : "",
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
                {state === "active" ? (
                  <div className="ml-auto flex shrink-0 gap-[6px]">
                    {canIssue ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={issue.pending}
                        onClick={() => setConfirm({ kind: "rotate", key: k })}
                      >
                        교체
                      </Button>
                    ) : null}
                    {mayRevoke ? (
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={issue.pending}
                        onClick={() => setConfirm({ kind: "revoke", key: k })}
                      >
                        회수
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {canIssue ? (
        <ModalForm className="mt-[16px] border-t border-grey-100 pt-[16px]">
          <Field id="key-name" label="새 키 이름">
            <Input
              id="key-name"
              placeholder="예: rn SSO exchange (dev)"
              value={name}
              disabled={issue.pending}
              onChange={(ev) => setName(ev.target.value)}
            />
          </Field>
          <Field id="key-expiry" label="만료">
            <Select
              id="key-expiry"
              value={mode}
              disabled={issue.pending}
              onChange={(ev) => setMode(ev.target.value as ExpiryMode)}
              options={[
                { value: "default", label: "365일 (기본)" },
                { value: "date", label: "날짜 지정" },
                ...(mayUnlimited ? [{ value: "unlimited", label: "무기한" }] : []),
              ]}
            />
          </Field>
          {mode === "date" ? (
            <Field id="key-date" label="만료일">
              <Input
                id="key-date"
                type="date"
                min={minDate}
                max={maxDate}
                value={date}
                disabled={issue.pending}
                onChange={(ev) => setDate(ev.target.value)}
              />
            </Field>
          ) : null}
        </ModalForm>
      ) : null}

      {!isActive ? (
        <p className="m-0 mt-[14px] text-[12px] text-grey-500">
          비활성 계정에는 키를 발급하거나 교체할 수 없습니다.
        </p>
      ) : null}

      {confirm?.kind === "rotate" ? (
        <ConfirmDialog
          title="API 키 교체"
          message={`${confirm.key.name} (${confirm.key.masked}) 를 교체합니다. 새 키가 발급되고, 이전 키는 회수하기 전까지 함께 유효합니다.`}
          confirmLabel="교체"
          tone="primary"
          onConfirm={() => onRotate(confirm.key)}
          onClose={() => setConfirm(null)}
        />
      ) : null}
      {confirm?.kind === "revoke" ? (
        <ConfirmDialog
          title="API 키 회수"
          message={`${confirm.key.name} (${confirm.key.masked}) 를 회수합니다. 이 키를 쓰는 시스템은 즉시 인증에 실패합니다.`}
          confirmLabel="회수"
          reason={{ label: "회수 사유 (선택)", placeholder: "예: 평문 유실" }}
          onConfirm={(reason) => onRevoke(confirm.key, reason)}
          onClose={() => setConfirm(null)}
        />
      ) : null}
      {confirm?.kind === "discard" ? (
        <ConfirmDialog
          title="새 키를 복사하지 않았습니다"
          message="닫으면 이 키의 평문을 다시 볼 수 없습니다. 평문이 없는 키는 회수하고 새로 발급해야 합니다."
          confirmLabel="복사하지 않고 닫기"
          onConfirm={() => {
            onClose();
            return Promise.resolve();
          }}
          onClose={() => setConfirm(null)}
        />
      ) : null}
    </Modal>
  );
}
